/**
 * Prépare le formulaire de paiement (carte + wallets) QUAND il est affiché,
 * et le range quand il disparaît. UNE règle, pour toutes les pages de
 * paiement à étapes (billetterie, adhésion, boutique…).
 *
 * 🔴 POURQUOI (prod, 2026-09-25) — événement gratuit + pourboire : ni champ
 * carte, ni bouton PayPal, seulement Apple/Google Pay. Les pages préparaient
 * le paiement à un MOMENT (« on arrive sur le récap »), pas selon un ÉTAT
 * (« le bloc paiement est à l'écran ») :
 *   - à 0 €, le bloc n'était pas affiché → la préparation échouait ;
 *   - le pourboire l'affichait ensuite, mais plus rien ne la relançait ;
 *   - à l'inverse, repasser à 0 € puis remettre un montant laissait une
 *     carte accrochée à un bloc disparu.
 * Ici, c'est l'état qui décide : chaque fois que `visible` passe à vrai, on
 * (re)monte ; chaque fois qu'il passe à faux, on démonte. Le montage attend
 * que Vue ait rendu le bloc (`flush: "post"` + une frame).
 *
 * `cle` : ce qui impose de remonter la carte alors que le bloc reste affiché
 * (changement de prestataire carte, bascule don unique ↔ mensuel…).
 */
import { nextTick, onBeforeUnmount, ref, watch, type Ref } from "vue";

export function usePaymentFormLifecycle(opts: {
  /** Le bloc paiement est-il rendu dans la page ? */
  visible: Readonly<Ref<boolean>>;
  /** Monte carte + wallets. Renvoie `true` si le champ carte est prêt. */
  monter: () => Promise<boolean>;
  /** Démonte tout (instances Stripe + PayPal). */
  demonter: () => void;
  /** Valeur dont le changement impose un remontage (bloc déjà affiché). */
  cle?: () => unknown;
}) {
  /** `true` quand le champ carte est réellement monté. */
  const pret = ref(false);

  // Les MONTAGES sont sérialisés : deux changements rapprochés (pourboire
  // tapé vite, retour/avance d'étape) ne doivent pas monter deux fois en
  // parallèle dans le même conteneur.
  let file: Promise<void> = Promise.resolve();
  // Le DÉMONTAGE, lui, est IMMÉDIAT et invalide tout montage en cours
  // (`generation`). En file d'attente, il passait après un montage lent
  // (wallets) et éteignait le bouton PayPal tout juste remonté : don retiré
  // puis remis → bouton PayPal absent (2026-09-25).
  let generation = 0;
  let detruit = false;

  function planifierMontage(): Promise<void> {
    const g = generation;
    const tache = async () => {
      if (detruit || g !== generation || !opts.visible.value) return;
      await nextTick();
      await new Promise<void>((r) => requestAnimationFrame(() => r()));
      if (detruit || g !== generation || !opts.visible.value) return;
      const ok = await opts.monter();
      if (g === generation) pret.value = ok;
    };
    file = file.then(tache, tache);
    return file;
  }

  function demonterMaintenant(): void {
    generation++;
    pret.value = false;
    opts.demonter();
  }

  watch(
    () => opts.visible.value,
    (visible) => (visible ? planifierMontage() : demonterMaintenant()),
    { flush: "post", immediate: true },
  );

  if (opts.cle) {
    watch(opts.cle, () => planifierMontage(), { flush: "post" });
  }

  onBeforeUnmount(() => {
    detruit = true;
  });

  return {
    pret,
    /** Bouton « Réessayer » : relance le montage (les erreurs sont effacées au montage). */
    reessayer: () => planifierMontage(),
  };
}
